import { RouteMapping } from '@reliance/types/models/Call';
import { Facility } from '@reliance/types/models/Facility';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import api from '../../common/api';
import ConfirmModal from '../../common/dialogs/ConfirmModal';
import { formatPhoneNumber } from '../../format/PhoneNumber';
import { useCallRouteMappings } from '../../hooks/CallRoute';
import { useNotifyTabLoading } from '../../hooks/useTabLoadingReporter';
import BaseHtmlTable from '../../table/BaseHtmlTable';
import AddMappingModal from './AddMappingModal';

type MappingListProps = {
  facility: Facility;
};

type Location = {
  city: string;
  state: string;
  country: string;
}

const resolveLocation = (number: string, locations: { [key: string]: Location }) => {
  if (locations[number] == null) {
    return '';
  }

  return (
    <span>
      ({locations[number].city && locations[number].city}, {locations[number].state && locations[number].state} { locations[number].country })
    </span>
  );
};

const MappingList: React.FC<MappingListProps> = (props) => {
  const [addMapping, setAddMapping] = useState(false);
  const [editingMapping, setEditingMapping] = useState<RouteMapping | undefined>();
  const [deletingRouteMapping, setDeletingRouteMapping] = useState<RouteMapping>();

  const filters = useMemo(() => ({ facilityId: props.facility.id }), [props.facility]);
  const callRouteMappings = useCallRouteMappings(filters);

  useNotifyTabLoading('routing', callRouteMappings.loading);

  const locations: { [key: string]: Location } = (callRouteMappings.result?.supportingData?.locations || []);

  const columnHelper = createColumnHelper<RouteMapping>();
  const columnDefs = [
    columnHelper.accessor('origination', {
      header: () => 'Origination',
      cell: (data) =>
        <span>{data.getValue()} {resolveLocation(data.row.original.origination, locations)}</span>
    }),
    columnHelper.accessor('destination', {
      header: () => 'Destination',
      cell: (data) =>
      <span>{data.getValue()} {resolveLocation(data.row.original.destination, locations)}</span>
    }),
    columnHelper.accessor('billingCategory', {
      header: () => 'Billing Category'
    }),
    columnHelper.accessor('callRoute.name', {
      header: () => 'Route Name'
    }),
    columnHelper.accessor('callRoute.type', {
      header: () => 'Route Type'
    }),
    columnHelper.accessor('callRoute.dialing', {
      header: () => 'Dialing'
    }),
    columnHelper.display({
      id: 'options',
      cell: (data) => (
        <>
          <Button variant="outline-primary" size="sm" className="me-1" onClick={() => setEditingMapping(data.row.original)}>
            Edit
          </Button>
          <Button variant="danger" size="sm" onClick={() => setDeletingRouteMapping(data.row.original)}>Remove</Button>
        </>
      )
    })
  ];

  const rows = (callRouteMappings.result?.data || []);

  return (
    <>
      <Button variant="primary" onClick={() => setAddMapping(true)}>Add Call Route Mapping</Button>

      {addMapping && (
        <AddMappingModal
          onClose={(success) => {
            if (success) callRouteMappings.execute();
            setAddMapping(false);
          }}
        />
      )}

      {editingMapping && (
        <AddMappingModal
          mapping={editingMapping}
          onClose={(success) => {
            if (success) callRouteMappings.execute();
            setEditingMapping(undefined);
          }}
        />
      )}

      {deletingRouteMapping &&
        <ConfirmModal
          title="Remove Route Mapping"
          show={true}
          onAccept={() =>
            api.delete(`/call/route/mapping/${deletingRouteMapping.id}`)
              .then(callRouteMappings.execute)
              .finally(() => setDeletingRouteMapping(null))
          }
          onClose={() => setDeletingRouteMapping(null)}>
          Are you sure you wish to remove the route mapping from <strong>{deletingRouteMapping.origination}</strong> to <strong>{deletingRouteMapping.destination}</strong>?
        </ConfirmModal>
      }

      {callRouteMappings.result?.supportingData?.facilityBasisNumber &&
        <h3>Basis Number: {formatPhoneNumber(callRouteMappings.result.supportingData.facilityBasisNumber.ani)}</h3>
      }

      <div className="mt-3">
        <BaseHtmlTable
          columnDefs={columnDefs}
          rows={rows} />
      </div>
    </>
  );
};

export default MappingList;
